import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

// Types
export interface Creator {
  id: string
  username: string
  displayName: string
  bio?: string
  profileImage?: string
  followerCount: number
  followingCount: number
  recipeCount: number
  isVerified: boolean
  isFollowing?: boolean
  socialLinks?: {
    instagram?: string
    youtube?: string
    twitter?: string
    website?: string
  }
  specialties?: string[]
  joinedAt: string
}

export interface CreatorFilters {
  specialties: string[]
  verified: boolean | null
  minFollowers: number | null
  sortBy: 'follower_count' | 'recipe_count' | 'created_at' | 'name'
  sortOrder: 'asc' | 'desc'
}

export interface CreatorState {
  creators: Creator[]
  featured: Creator[]
  currentCreator: Creator | null
  filters: CreatorFilters
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    hasNextPage: boolean
  }
}

const initialState: CreatorState = {
  creators: [],
  featured: [],
  currentCreator: null,
  filters: {
    specialties: [],
    verified: null,
    minFollowers: null,
    sortBy: 'follower_count',
    sortOrder: 'desc',
  },
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    hasNextPage: false,
  },
}

// Async thunks
export const fetchCreators = createAsyncThunk(
  'creators/fetchCreators',
  async (params: { page?: number; filters?: Partial<CreatorFilters> } = {}) => {
    // This would be replaced with actual API call
    // const response = await creatorApi.getCreators(params)
    // return response.data
    
    // Mock data for now
    const mockCreators: Creator[] = [
      {
        id: '1',
        username: 'james_hoffmann',
        displayName: 'James Hoffmann',
        bio: 'Coffee expert, author, and YouTuber',
        profileImage: '/images/creators/james.jpg',
        followerCount: 125000,
        followingCount: 42,
        recipeCount: 156,
        isVerified: true,
        specialties: ['Pour Over', 'Espresso', 'Cupping'],
        joinedAt: '2020-01-15T00:00:00Z',
      },
    ]
    
    return {
      creators: mockCreators,
      pagination: {
        page: params.page || 1,
        limit: 20,
        total: 1,
        hasNextPage: false,
      },
    }
  }
)

export const fetchCreatorById = createAsyncThunk(
  'creators/fetchCreatorById',
  async (creatorId: string) => {
    // Mock data for now
    const mockCreator: Creator = {
      id: creatorId,
      username: 'james_hoffmann',
      displayName: 'James Hoffmann',
      bio: 'Coffee expert, author, and YouTuber',
      profileImage: '/images/creators/james.jpg',
      followerCount: 125000,
      followingCount: 42,
      recipeCount: 156,
      isVerified: true,
      specialties: ['Pour Over', 'Espresso', 'Cupping'],
      joinedAt: '2020-01-15T00:00:00Z',
    }
    
    return mockCreator
  }
)

export const followCreator = createAsyncThunk(
  'creators/followCreator',
  async (creatorId: string) => {
    // Mock API call
    return { creatorId, isFollowing: true }
  }
)

export const unfollowCreator = createAsyncThunk(
  'creators/unfollowCreator',
  async (creatorId: string) => {
    // Mock API call
    return { creatorId, isFollowing: false }
  }
)

// Slice
const creatorSlice = createSlice({
  name: 'creators',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<CreatorFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    setCurrentCreator: (state, action: PayloadAction<Creator | null>) => {
      state.currentCreator = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch creators
      .addCase(fetchCreators.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCreators.fulfilled, (state, action) => {
        state.loading = false
        state.creators = action.payload.creators
        state.pagination = action.payload.pagination
      })
      .addCase(fetchCreators.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch creators'
      })
      
      // Fetch creator by ID
      .addCase(fetchCreatorById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCreatorById.fulfilled, (state, action) => {
        state.loading = false
        state.currentCreator = action.payload
      })
      .addCase(fetchCreatorById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch creator'
      })
      
      // Follow creator
      .addCase(followCreator.fulfilled, (state, action) => {
        const { creatorId, isFollowing } = action.payload
        
        // Update in creators list
        const creatorIndex = state.creators.findIndex(c => c.id === creatorId)
        if (creatorIndex !== -1) {
          state.creators[creatorIndex].isFollowing = isFollowing
          state.creators[creatorIndex].followerCount += 1
        }
        
        // Update current creator
        if (state.currentCreator?.id === creatorId) {
          state.currentCreator.isFollowing = isFollowing
          state.currentCreator.followerCount += 1
        }
      })
      
      // Unfollow creator
      .addCase(unfollowCreator.fulfilled, (state, action) => {
        const { creatorId, isFollowing } = action.payload
        
        // Update in creators list
        const creatorIndex = state.creators.findIndex(c => c.id === creatorId)
        if (creatorIndex !== -1) {
          state.creators[creatorIndex].isFollowing = isFollowing
          state.creators[creatorIndex].followerCount -= 1
        }
        
        // Update current creator
        if (state.currentCreator?.id === creatorId) {
          state.currentCreator.isFollowing = isFollowing
          state.currentCreator.followerCount -= 1
        }
      })
  },
})

export const { setFilters, clearFilters, setCurrentCreator, clearError } = creatorSlice.actions
export default creatorSlice.reducer 